<template>
    <div>
        <Loader :loader="isLoading" v-if="isLoading" />
        <div v-else>
            <div>
                <b-media class="mb-4" v-for="pdi in pdis" :key="pdi.id">
                    <avatar
                        :size="42"
                        :src="pdi.user.picture"
                        :username="pdi.user.name"
                        class="hidden-sm-down"
                        slot="aside"
                    />
                    <div class="d-flex justify-content-between">
                        <div class="mr-2">
                            <p class="mb-0 text-justify text-small-sm-down">
                                <strong>{{ pdi.title }}</strong>
                            </p>
                        </div>
                    </div>

                    <b-media right-align vertical-align="center">
                        <div slot="aside">
                            <b-dropdown
                                id="btn-tab-home-pdi-actions"
                                size="sm"
                                variant="link"
                                :no-caret="true"
                                :dropleft="true"
                                class="ml-1"
                                v-if="hasPermission"
                            >
                                <template slot="button-content">
                                    <i class="fa fa-ellipsis-v fa-lg"></i>
                                </template>
                                <b-dropdown-item id="btn-tab-home-pdi-edit" @click="openEditPdiModal(pdi.id)">{{
                                    $t('form.button.edit')
                                }}</b-dropdown-item>
                                <b-dropdown-item id="btn-tab-home-pdi-delete" @click="deletePdi(pdi.id)">{{
                                    $t('form.button.delete')
                                }}</b-dropdown-item>
                            </b-dropdown>
                        </div>
                        <div class="d-flex">
                            <p class="mb-0 hidden-sm-down">
                                <small class="text-muted">{{ pdi.user.name }}</small>
                            </p>
                        </div>
                        <div>
                            <b-progress height="5px" :value="pdi.performance" />
                        </div>
                        <div class="d-flex justify-content-between mb-2">
                            <p class="mb-0 mt-2">
                                <b-badge variant="light"></b-badge>
                            </p>
                            <p class="mb-0 ml-auto">
                                <strong class="text-muted">{{ pdi.performance }}%</strong>
                            </p>
                        </div>
                    </b-media>

                    <div class="text-right">
                        <b-button
                            id="btn-home-pdi-tab-new-action-plan"
                            class="mr-3"
                            size="sm"
                            type="button"
                            :variant="'outline-primary'"
                            @click="openNewActionPlanModal(pdi.id)"
                        >
                            <i class="icon fa fa-plus-circle mr-1"></i>
                            {{ $t('profile.targetsTable.newActionPlan') }}
                        </b-button>
                        <b-button
                            size="sm"
                            type="button"
                            variant="outline-primary"
                            v-b-toggle="`collapse-targets-children-${pdi.id}`"
                        >
                            <i class="icon fa fa-angle-down mr-1"></i>
                            {{ $t('profile.pdiTable.actionPlans') }}
                        </b-button>
                    </div>
                    <b-collapse :id="`collapse-targets-children-${pdi.id}`">
                        <div v-if="pdi.action_plans.length">
                            <action-plans
                                class="mt-1"
                                :data="pdi.action_plans"
                                :currentValue="pdi.pdi_checkin"
                                :userId="pdi.user_id"
                                :currentUser="currentUser"
                                :pdiId="pdi.id"
                                @actionPlanUpdated="getUserPdis"
                            />
                        </div>
                        <div v-else>
                            <b-alert class="mt-3" show variant="info">{{
                                $t('profile.pdiTable.emptyActionPlan')
                            }}</b-alert>
                        </div>
                    </b-collapse>
                    <new-action-plan-modal :pdiId="pdi.id" @reloadPdis="getUserPdis" />
                    <edit-pdi-modal :pdiId="pdi.id" @reloadPdis="getUserPdis" />
                </b-media>
            </div>
            <b-alert show variant="info" v-show="!pdis.length && !isLoading">{{
                $t('profile.pdiTable.empty')
            }}</b-alert>
        </div>
    </div>
</template>

<script>
import i18n from '@/i18n'
import Swal from 'sweetalert2'
import Avatar from 'vue-avatar'
import Loader from '@/views/components/loader'
import ActionPlans from './children/action-plans'
import { formatPercentage } from '@/lib/utils'
import { getUserPdis, deletePdi } from './api'
import { success, danger } from '@/lib/notification'
import { deleteConfirmationWithLoading } from '@/config/sweet-alert'
import { userPermissionsMixin } from '@/mixins'
import { Edit as EditPdiModal, NewActionPlan as NewActionPlanModal } from '@/views/pdi'

export default {
    name: 'PDI',
    props: {
        user: {
            type: Object,
            required: true,
        },
        periodIds: {
            type: Array,
            required: true,
        },
    },
    mixins: [userPermissionsMixin],
    components: {
        Avatar,
        Loader,
        ActionPlans,
        EditPdiModal,
        NewActionPlanModal,
    },
    data() {
        return {
            pdis: [],
            isLoading: true,
        }
    },
    mounted() {
        this.getUserPdis()
        this.$store.watch(
            (state) => state.pdi.reloadPDIs,
            (reloadPDIs) => {
                if (reloadPDIs) {
                    this.getUserPdis()
                }
            }
        )
    },
    methods: {
        getUserPdis() {
            this.isLoading = true
            getUserPdis(this.user.id, this.periodIds)
                .then((data) => {
                    this.pdis = data
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        formatPercentage(number) {
            return formatPercentage(number)
        },
        openEditPdiModal(id) {
            if (this.hasPermission) {
                this.$bvModal.show(`edit-pdi-modal${id}`)
            } else {
                return this.$router.push('/v2/pages/error/403')
            }
        },
        deletePdi(id) {
            Swal({
                ...deleteConfirmationWithLoading,
                preConfirm: () => {
                    deletePdi(id)
                        .then(() => {
                            this.notificationPdiDeletedSuccessfully()
                            this.getUserPdis()
                        })
                        .catch((e) => {
                            console.log(e)
                            this.notificationError()
                        })
                },
            })
        },
        openNewActionPlanModal(pdiId) {
            this.$bvModal.show(`new-action-plan-modal${pdiId}`)
        },
        notificationPdiDeletedSuccessfully() {
            success(
                i18n.t('notification.deletedSuccessfully', {
                    item: i18n.t('pdis.pdi'),
                })
            )
        },
        notificationError() {
            danger()
        },
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser.user
        },
        hasPermission() {
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_values_master']
            return (
                this.user.id == this.currentUser.id ||
                this.user.manager.id == this.currentUser.id ||
                this.userHasPermission(this.currentUser, availableRoles, [])
            )
        },
    },
    watch: {
        'user.id'(newUserID, oldUserID) {
            if (newUserID != oldUserID) {
                this.getUserPdis()
            }
        },
        periodIds(oldValue, newValue) {
            if (oldValue.length != newValue.length) {
                this.getUserPdis()
            }
        },
    },
}
</script>

<style>
@media (max-width: 575px) {
    .text-small-sm-down {
        font-size: 12px !important;
    }
    .hidden-sm-down {
        display: none !important;
    }
}
</style>
